import type { FC } from "react";
import type { RenderMessage } from "./types";
import { BlockRenderer } from "./BlockRenderer";
import type { ViewPreset } from "./ConversationRenderer";

type MessageRendererProps = {
  message: RenderMessage;
  view?: ViewPreset;
};

export const MessageRenderer: FC<MessageRendererProps> = ({
  message,
  view = "desktop",
}) => {
  const { role, blocks } = message;

  if (role === "user") {
    return <UserMessage blocks={blocks} />;
  }

  if (role === "assistant") {
    return <AssistantMessage blocks={blocks} />;
  }

  if (role === "system") {
    return <SystemMessage blocks={blocks} />;
  }

  if (role === "tool") {
    return <ToolMessage blocks={blocks} />;
  }

  // Exhaustive check
  const _exhaustive: never = role;
  return null;
};

type MessageContentProps = {
  blocks: RenderMessage["blocks"];
};

/**
 * User message: Right-aligned, rounded bubble with bg-muted
 * Container width is controlled by parent ConversationRenderer
 */
const UserMessage: FC<MessageContentProps> = ({ blocks }) => {
  return (
    <div data-role="user">
      <div className="user-message-bubble">
        {blocks.map((block, index) => (
          <BlockRenderer key={index} block={block} />
        ))}
      </div>
    </div>
  );
};

/**
 * Assistant message: Left-aligned, full-width, no bubble background
 */
const AssistantMessage: FC<MessageContentProps> = ({ blocks }) => {
  return (
    <div data-role="assistant">
      <div className="assistant-message-content">
        {blocks.map((block, index) => (
          <BlockRenderer key={index} block={block} />
        ))}
      </div>
    </div>
  );
};

/**
 * System message: Centered, muted styling
 */
const SystemMessage: FC<MessageContentProps> = ({ blocks }) => {
  return (
    <div data-role="system">
      <div className="system-message-content">
        {blocks.map((block, index) => (
          <BlockRenderer key={index} block={block} />
        ))}
      </div>
    </div>
  );
};

/**
 * Tool message: Similar to assistant but with distinct styling
 */
const ToolMessage: FC<MessageContentProps> = ({ blocks }) => {
  return (
    <div data-role="tool">
      <div className="tool-message-content">
        {blocks.map((block, index) => (
          <BlockRenderer key={index} block={block} />
        ))}
      </div>
    </div>
  );
};
